import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const SERVICE_TYPES = [
  'Oil Change',
  'Filter Replacement',
  'Service Interval Reset',
  'Complete Service Package'
];

export default function BookAppointment() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    service_type: '',
    date: '',
    time: '',
    notes: '',
    has_own_oil: true,
    preferred_oil_type: '',
    preferred_oil_brand: '',
    needs_recommendation: false,
    include_coolant_change: false,
    include_brake_fluid_change: false,
    payment_method: '',
    selected_products: []
  });
  const [loading, setLoading] = useState(false);
  
  // Product catalog state
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState({ categories: [], oil_types: [], service_charge: 10000 });
  const [productSearch, setProductSearch] = useState('');
  const [selectedOilType, setSelectedOilType] = useState('');
  
  // Track which categories user wants to change
  const [changeEngineOil, setChangeEngineOil] = useState(false);
  const [changeOilFilter, setChangeOilFilter] = useState(false);
  const [changeCoolant, setChangeCoolant] = useState(false);
  const [changeBrakeFluid, setChangeBrakeFluid] = useState(false);
  const [changeTransmissionOil, setChangeTransmissionOil] = useState(false);

  useEffect(() => {
    fetchVehicles();
    fetchProducts();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
      
      if (response.data.length === 0) {
        toast.error('Please add a vehicle first');
        navigate('/vehicles/add');
      }
    } catch (error) {
      toast.error('Failed to load vehicles');
    }
  };

  const fetchProducts = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/products/categories`)
      ]);
      setProducts(productsRes.data);
      setProductCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  // Get products by category with optional oil type filter
  const getProductsByCategory = (category, oilType = null) => {
    let filtered = products.filter(p => p.category === category && p.status === 'active');
    if (oilType && category === 'engine_oil') {
      filtered = filtered.filter(p => p.oil_type === oilType);
    }
    // Apply search filter
    if (productSearch) {
      const search = productSearch.toLowerCase();
      filtered = filtered.filter(p => 
        p.brand_name.toLowerCase().includes(search) ||
        (p.code && p.code.toLowerCase().includes(search))
      );
    }
    return filtered;
  };

  // Add or update selected product
  const selectProduct = (product) => {
    const existing = formData.selected_products.find(p => p.category === product.category);
    let updatedProducts;
    
    if (existing && existing.product_id === product.id) {
      // Deselect if clicking the same product
      updatedProducts = formData.selected_products.filter(p => p.category !== product.category);
    } else {
      // Replace product in same category or add new
      updatedProducts = formData.selected_products.filter(p => p.category !== product.category);
      updatedProducts.push({
        product_id: product.id,
        product_name: `${product.brand_name}${product.code ? ` (${product.code})` : ''}`,
        category: product.category,
        price: product.price,
        quantity: 1,
        image_url: product.image_url
      });
    }
    
    setFormData({ ...formData, selected_products: updatedProducts });
  };

  // Check if product is selected
  const isProductSelected = (productId) => {
    return formData.selected_products.some(p => p.product_id === productId);
  };

  // Get selected product for a category
  const getSelectedProductForCategory = (category) => {
    return formData.selected_products.find(p => p.category === category);
  };

  // Calculate total amount
  const calculateTotal = () => {
    const productTotal = formData.selected_products.reduce((sum, p) => sum + p.price * p.quantity, 0);
    return productCategories.service_charge + productTotal;
  };

  // Get category label
  const getCategoryLabel = (cat) => {
    const labels = {
      engine_oil: 'Engine Oil',
      oil_filter: 'Oil Filter',
      coolant: 'Coolant',
      brake_fluid: 'Brake Fluid',
      transmission_oil: 'Transmission Oil'
    };
    return labels[cat] || cat;
  };

  const getOilTypeLabel = (type) => {
    const labels = {
      synthetic: 'Synthetic',
      semi_synthetic: 'Semi-Synthetic',
      mineral: 'Mineral'
    };
    return labels[type] || type;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        notes: formData.notes || null,
        preferred_oil_type: formData.has_own_oil ? null : (formData.preferred_oil_type || null),
        selected_products: formData.selected_products.map(p => ({
          product_id: p.product_id,
          quantity: p.quantity
        }))
      };
      
      await axios.post(`${API}/appointments`, payload);
      toast.success('Appointment booked successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const selectedVehicle = vehicles.find(v => v.id === formData.vehicle_id);

  // Product Selection Card Component
  const ProductCard = ({ product }) => {
    const isSelected = isProductSelected(product.id);
    return (
      <div
        onClick={() => selectProduct(product)}
        className={`border-2 p-4 rounded-sm cursor-pointer transition-all ${
          isSelected
            ? 'border-primary bg-primary/5 shadow-sm'
            : 'border-border hover:border-primary/50'
        }`}
      >
        <div className="flex items-center gap-3">
          {product.image_url ? (
            <img 
              src={product.image_url.startsWith('/') ? `${API.replace('/api', '')}${product.image_url}` : product.image_url}
              alt={product.brand_name}
              className="w-16 h-16 object-cover rounded"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
              No img
            </div>
          )}
          <div className="flex-1">
            <h4 className="font-bold text-foreground">{product.brand_name}</h4>
            {product.code && <p className="text-sm text-muted-foreground font-mono">{product.code}</p>}
            {product.oil_type && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                {getOilTypeLabel(product.oil_type)}
              </span>
            )}
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-green-600">₦{product.price.toLocaleString()}</p>
            {isSelected && (
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1 ml-auto">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Category Selection Section
  const CategorySection = ({ category, isExpanded, setExpanded, children }) => {
    const selectedProduct = getSelectedProductForCategory(category);
    
    return (
      <div className="border-2 border-border rounded-sm overflow-hidden">
        <div 
          onClick={() => setExpanded(!isExpanded)}
          className={`p-4 cursor-pointer transition-all ${isExpanded ? 'bg-primary/5' : 'bg-white hover:bg-gray-50'}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">
                {category === 'engine_oil' && '🛢️'}
                {category === 'oil_filter' && '🔧'}
                {category === 'coolant' && '❄️'}
                {category === 'brake_fluid' && '🛑'}
                {category === 'transmission_oil' && '⚙️'}
              </span>
              <div>
                <h3 className="font-bold text-foreground">{getCategoryLabel(category)}</h3>
                {selectedProduct ? (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ {selectedProduct.product_name} - ₦{selectedProduct.price.toLocaleString()}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {isExpanded ? 'Select a product' : 'Click to add'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedProduct && !isExpanded && (
                <span className="text-green-600 font-bold">₦{selectedProduct.price.toLocaleString()}</span>
              )}
              <svg 
                className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 border-t border-border bg-gray-50">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
              className="font-bold uppercase text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-12 h-12 rounded-sm font-heading font-black text-xl ${
                  s <= step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {s}
                </div>
                {s < 4 && (
                  <div className={`w-16 h-1 ${
                    s < step ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-center mt-4">
            <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              {step === 1 && 'Select Vehicle'}
              {step === 2 && 'Choose Service'}
              {step === 3 && 'Select Products'}
              {step === 4 && 'Pick Date & Time'}
            </p>
          </div>
        </div>

        <div className="bg-white border-2 border-border rounded-sm shadow-hard p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Select Vehicle */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-3xl font-heading font-bold text-foreground mb-6">Select Your Vehicle</h2>
                <div className="grid gap-4">
                  {vehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      onClick={() => setFormData({ ...formData, vehicle_id: vehicle.id })}
                      data-testid="vehicle-option"
                      className={`border-2 p-6 rounded-sm cursor-pointer transition-all ${
                        formData.vehicle_id === vehicle.id
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-heading font-bold text-foreground">
                            {vehicle.make} {vehicle.model}
                          </h3>
                          <p className="text-sm text-muted-foreground font-medium">
                            {vehicle.year} • {vehicle.license_plate}
                          </p>
                        </div>
                        {formData.vehicle_id === vehicle.id && (
                          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  disabled={!formData.vehicle_id}
                  onClick={() => setStep(2)}
                  data-testid="step1-next-btn"
                  className="btn-primary w-full h-12 text-lg"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Select Service */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-heading font-bold text-foreground">Choose Service</h2>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(1)}
                    data-testid="step2-back-btn"
                    className="font-bold uppercase text-sm"
                  >
                    ← Back
                  </Button>
                </div>
                {selectedVehicle && (
                  <div className="bg-accent p-4 rounded-sm mb-6">
                    <p className="text-sm font-medium text-muted-foreground">Selected Vehicle:</p>
                    <p className="font-heading font-bold text-foreground">
                      {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.license_plate})
                    </p>
                  </div>
                )}
                <div className="grid gap-4">
                  {SERVICE_TYPES.map((service) => (
                    <div
                      key={service}
                      onClick={() => setFormData({ ...formData, service_type: service })}
                      data-testid="service-option"
                      className={`border-2 p-6 rounded-sm cursor-pointer transition-all ${
                        formData.service_type === service
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-heading font-bold text-foreground">{service}</h3>
                        {formData.service_type === service && (
                          <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  disabled={!formData.service_type}
                  onClick={() => setStep(3)}
                  data-testid="step2-next-btn"
                  className="btn-primary w-full h-12 text-lg"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 3: Select Products */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-heading font-bold text-foreground">Select Products</h2>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(2)}
                    data-testid="step3-back-btn"
                    className="font-bold uppercase text-sm"
                  >
                    ← Back
                  </Button>
                </div>

                {/* Service Summary */}
                <div className="bg-accent p-4 rounded-sm space-y-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Vehicle:</p>
                    <p className="font-heading font-bold text-foreground">
                      {selectedVehicle?.make} {selectedVehicle?.model}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Service:</p>
                    <p className="font-heading font-bold text-foreground">{formData.service_type}</p>
                  </div>
                </div>

                {/* Product Search */}
                <div>
                  <Input
                    type="text"
                    placeholder="Search products by name or code..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="input-field"
                    data-testid="product-search"
                  />
                </div>

                {/* Service Charge Info */}
                <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-blue-800">Base Service Charge</p>
                      <p className="text-sm text-blue-600">Standard service fee for all bookings</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-800">₦{productCategories.service_charge?.toLocaleString()}</p>
                  </div>
                </div>

                {/* Own Oil Option */}
                <div className="border-2 border-border rounded-sm p-4">
                  <Label className="font-bold uppercase text-sm tracking-wide mb-3 block">
                    Do you have your own products?
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => {
                        setFormData({ ...formData, has_own_oil: true, selected_products: [] });
                        setChangeEngineOil(false);
                        setChangeOilFilter(false);
                        setChangeCoolant(false);
                        setChangeBrakeFluid(false);
                        setChangeTransmissionOil(false);
                      }}
                      className={`border-2 p-4 rounded-sm cursor-pointer transition-all text-center ${
                        formData.has_own_oil
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="has-own-oil-yes"
                    >
                      <p className="font-bold text-foreground">Yes, I have my own</p>
                      <p className="text-xs text-muted-foreground">I'll provide oil & filter</p>
                    </div>
                    <div
                      onClick={() => setFormData({ ...formData, has_own_oil: false })}
                      className={`border-2 p-4 rounded-sm cursor-pointer transition-all text-center ${
                        !formData.has_own_oil
                          ? 'border-secondary bg-secondary/5'
                          : 'border-border hover:border-secondary/50'
                      }`}
                      data-testid="has-own-oil-no"
                    >
                      <p className="font-bold text-foreground">No, I need products</p>
                      <p className="text-xs text-muted-foreground">Select from catalog below</p>
                    </div>
                  </div>
                </div>

                {/* Product Categories - Progressive Display */}
                {!formData.has_own_oil && (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-muted-foreground">
                      Click on each category to select products:
                    </p>

                    {/* Engine Oil */}
                    <CategorySection 
                      category="engine_oil" 
                      isExpanded={changeEngineOil} 
                      setExpanded={setChangeEngineOil}
                    >
                      {/* Oil Type Selection */}
                      <div className="mb-4">
                        <Label className="font-bold text-sm mb-2 block">Select Oil Type</Label>
                        <div className="flex flex-wrap gap-2">
                          {productCategories.oil_types?.map(type => (
                            <Button
                              key={type}
                              type="button"
                              variant={selectedOilType === type ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setSelectedOilType(selectedOilType === type ? '' : type)}
                            >
                              {getOilTypeLabel(type)}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {getProductsByCategory('engine_oil', selectedOilType).map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                        {getProductsByCategory('engine_oil', selectedOilType).length === 0 && (
                          <p className="text-center text-muted-foreground py-4">No products found</p>
                        )}
                      </div>
                    </CategorySection>

                    {/* Oil Filter */}
                    <CategorySection 
                      category="oil_filter" 
                      isExpanded={changeOilFilter} 
                      setExpanded={setChangeOilFilter}
                    >
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {getProductsByCategory('oil_filter').map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                        {getProductsByCategory('oil_filter').length === 0 && (
                          <p className="text-center text-muted-foreground py-4">No products found</p>
                        )}
                      </div>
                    </CategorySection>

                    {/* Coolant */}
                    <CategorySection 
                      category="coolant" 
                      isExpanded={changeCoolant} 
                      setExpanded={setChangeCoolant}
                    >
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {getProductsByCategory('coolant').map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                        {getProductsByCategory('coolant').length === 0 && (
                          <p className="text-center text-muted-foreground py-4">No products found</p>
                        )}
                      </div>
                    </CategorySection>

                    {/* Brake Fluid */}
                    <CategorySection 
                      category="brake_fluid" 
                      isExpanded={changeBrakeFluid} 
                      setExpanded={setChangeBrakeFluid}
                    >
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {getProductsByCategory('brake_fluid').map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                        {getProductsByCategory('brake_fluid').length === 0 && (
                          <p className="text-center text-muted-foreground py-4">No products found</p>
                        )}
                      </div>
                    </CategorySection>

                    {/* Transmission Oil */}
                    <CategorySection 
                      category="transmission_oil" 
                      isExpanded={changeTransmissionOil} 
                      setExpanded={setChangeTransmissionOil}
                    >
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {getProductsByCategory('transmission_oil').map(product => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                        {getProductsByCategory('transmission_oil').length === 0 && (
                          <p className="text-center text-muted-foreground py-4">No products found</p>
                        )}
                      </div>
                    </CategorySection>
                  </div>
                )}

                {/* Total Amount Payable */}
                <div className="bg-green-50 border-2 border-green-300 p-6 rounded-sm">
                  <h3 className="font-bold text-green-800 uppercase text-sm mb-4">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-700">Service Charge</span>
                      <span className="font-medium text-green-800">₦{productCategories.service_charge?.toLocaleString()}</span>
                    </div>
                    {formData.selected_products.map((p, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-green-700">{p.product_name}</span>
                        <span className="font-medium text-green-800">₦{p.price.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="border-t border-green-300 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-bold text-green-800 text-lg">Total Amount Payable</span>
                        <span className="font-bold text-green-800 text-2xl">₦{calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() => setStep(4)}
                  data-testid="step3-next-btn"
                  className="btn-primary w-full h-12 text-lg"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 4: Date, Time & Notes */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-heading font-bold text-foreground">Schedule Appointment</h2>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(3)}
                    data-testid="step4-back-btn"
                    className="font-bold uppercase text-sm"
                  >
                    ← Back
                  </Button>
                </div>

                {/* Booking Summary */}
                <div className="bg-accent p-4 rounded-sm space-y-2">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Vehicle:</p>
                    <p className="font-heading font-bold text-foreground">
                      {selectedVehicle?.make} {selectedVehicle?.model}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Service:</p>
                    <p className="font-heading font-bold text-foreground">{formData.service_type}</p>
                  </div>
                  {formData.selected_products.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Selected Products:</p>
                      {formData.selected_products.map((p, idx) => (
                        <p key={idx} className="text-sm font-medium text-foreground">
                          • {p.product_name} - ₦{p.price.toLocaleString()}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="border-t border-border pt-2 mt-2">
                    <p className="text-sm font-medium text-muted-foreground">Total Amount:</p>
                    <p className="font-heading font-bold text-green-600 text-xl">
                      ₦{calculateTotal().toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="date" className="font-bold uppercase text-sm tracking-wide">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      required
                      data-testid="appointment-date-input"
                      className="input-field mt-2 bg-blue-50 border-2 border-blue-300 focus:border-blue-500"
                      min={new Date().toISOString().split('T')[0]}
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="time" className="font-bold uppercase text-sm tracking-wide">Time *</Label>
                    <Input
                      id="time"
                      type="time"
                      required
                      data-testid="appointment-time-input"
                      className="input-field mt-2 bg-blue-50 border-2 border-blue-300 focus:border-blue-500"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    />
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-sm">
                  <Label className="font-bold uppercase text-sm tracking-wide text-green-800 mb-3 block">
                    Select Payment Method
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div 
                      onClick={() => setFormData({ ...formData, payment_method: 'transfer' })}
                      className={`p-4 rounded-sm cursor-pointer border-2 transition-all text-center ${
                        formData.payment_method === 'transfer' 
                          ? 'border-green-500 bg-white shadow-sm' 
                          : 'border-green-200 bg-white hover:border-green-300'
                      }`}
                      data-testid="payment-transfer"
                    >
                      <div className="text-3xl mb-2">🏦</div>
                      <span className="font-bold text-foreground text-sm">Transfer</span>
                      <p className="text-xs text-muted-foreground mt-1">Verified by Secretary</p>
                    </div>
                    <div 
                      onClick={() => setFormData({ ...formData, payment_method: 'pos' })}
                      className={`p-4 rounded-sm cursor-pointer border-2 transition-all text-center ${
                        formData.payment_method === 'pos' 
                          ? 'border-green-500 bg-white shadow-sm' 
                          : 'border-green-200 bg-white hover:border-green-300'
                      }`}
                      data-testid="payment-pos"
                    >
                      <div className="text-3xl mb-2">💳</div>
                      <span className="font-bold text-foreground text-sm">POS</span>
                      <p className="text-xs text-muted-foreground mt-1">Verified by Secretary</p>
                    </div>
                    <div 
                      onClick={() => setFormData({ ...formData, payment_method: 'cash' })}
                      className={`p-4 rounded-sm cursor-pointer border-2 transition-all text-center ${
                        formData.payment_method === 'cash' 
                          ? 'border-green-500 bg-white shadow-sm' 
                          : 'border-green-200 bg-white hover:border-green-300'
                      }`}
                      data-testid="payment-cash"
                    >
                      <div className="text-3xl mb-2">💵</div>
                      <span className="font-bold text-foreground text-sm">Cash</span>
                      <p className="text-xs text-muted-foreground mt-1">Verified by Technician</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes" className="font-bold uppercase text-sm tracking-wide">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="appointment-notes-input"
                    className="input-field mt-2 min-h-[100px]"
                    placeholder="Any specific requirements or concerns?"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.date || !formData.time}
                  data-testid="appointment-submit-btn"
                  className="btn-primary w-full h-12 text-lg"
                >
                  {loading ? 'Booking...' : `Book Appointment - ₦${calculateTotal().toLocaleString()}`}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
